import { Currency, Token } from '@kodiak-finance/sdk-core'
import ERC20_ABI from 'abis/erc20.json'
import { SupportedChainId } from 'config/chains'
import { WAGMI_REFETCH_POLICY_NEVER } from 'config/RefetchPolicies'
import { useConnection } from 'hooks/useConnection'
import { useReadContractMultiple } from 'hooks/useReadContractMultiple'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { useMemo } from 'react'
import { isNativeCurrencyId } from 'utils/currencyId'
import { Abi, Address, erc20Abi, hexToBytes, hexToString, isHex } from 'viem'
import { useReadContract } from 'wagmi'

import { PROMOTE_TOKENS_BY_CHAIN } from '../../constants/tokens'
import { isAddress } from '../../utils'

// parse a name or symbol from a token response
const BYTES32_REGEX = /^0x[a-fA-F0-9]{64}$/

function parseStringOrBytes32(str: string | undefined, bytes32: string | undefined, defaultValue: string): string {
  if (str && str.length > 0) {
    return str
  }

  // Check for proper bytes string and valid terminator
  if (bytes32 && BYTES32_REGEX.test(bytes32) && isHex(bytes32, { size: 32 })) {
    try {
      // arrayify(bytes32)[31] === 0 check: Check the last byte for a null terminator (0x00)
      const bytes = hexToBytes(bytes32)
      if (bytes[31] === 0) {
        // parseBytes32String: Use viem's hexToString with trimming to replicate the behavior
        return hexToString(bytes32, { size: 32, trim: true })
      }
    } catch (e) {
      // If hex conversion or trimming fails, fall through to default
      console.error('Error processing bytes32 string:', e)
    }
  }

  return defaultValue
}

/**
 * Returns a Token from the tokenAddress.
 * Returns null if token is loading or null was passed.
 * Returns undefined if tokenAddress is invalid or token does not exist.
 */
export function useTokenFromNetworkInternal(tokenAddress: string | null | undefined): {
  token: Token | null | undefined
  isLoading: boolean
  error: string | undefined
} {
  const { chainId } = useConnection()
  const chainAllowed = chainId
  const formattedAddress = isAddress(tokenAddress)

  const contract = {
    address: formattedAddress as Address,
    abi: erc20Abi,
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: !!formattedAddress,
    },
  } as const

  const {
    data: decimals,
    isError: isErrorDecimals,
    isLoading: isLoadingDecimals,
    error: decimalsError,
  } = useReadContract({ ...contract, functionName: 'decimals' })
  const {
    data: name,
    isError: isErrorName,
    isLoading: isLoadingName,
    error: nameError,
  } = useReadContract({ ...contract, functionName: 'name' })
  const {
    data: symbol,
    isError: isErrorSymbol,
    isLoading: isLoadingSymbol,
    error: symbolError,
  } = useReadContract({ ...contract, functionName: 'symbol' })

  const isLoading = useMemo(
    () => isLoadingDecimals || isLoadingName || isLoadingSymbol,
    [isLoadingDecimals, isLoadingName, isLoadingSymbol]
  )

  const isError = useMemo(
    () => isErrorDecimals || isErrorName || isErrorSymbol,
    [isErrorDecimals, isErrorName, isErrorSymbol]
  )

  const error = useMemo(() => {
    if (!isError) return undefined

    const err = decimalsError?.message || nameError?.message || symbolError?.message
    return err ?? 'Unknown RPC error'
  }, [decimalsError, nameError, symbolError, isError])

  const token = useMemo(() => {
    if (typeof tokenAddress !== 'string' || !chainAllowed || !formattedAddress || isError) return undefined
    if (isLoading) return null
    if (decimals === undefined || !symbol || !name) return null

    return new Token(chainId, formattedAddress, decimals, symbol, name)
  }, [chainAllowed, chainId, decimals, formattedAddress, isError, isLoading, name, symbol, tokenAddress])

  return { token, isLoading, error }
}

// Token only
export function useTokenFromNetwork(tokenAddress: string | null | undefined): Token | null | undefined {
  const { token } = useTokenFromNetworkInternal(tokenAddress)
  return token
}

// Token + state
export function useTokenFromNetworkWithState(tokenAddress: string | null | undefined): {
  token: Token | null | undefined
  isLoading: boolean
  error: string | undefined
} {
  return useTokenFromNetworkInternal(tokenAddress)
}

export function useTokensFromNetwork(tokenAddresses: (string | null | undefined)[]): (Token | null | undefined)[] {
  const { chainId } = useConnection()
  const formattedAddresses = tokenAddresses.map(isAddress).map((item) => (item === false ? undefined : item))

  const { data: tokenNames } = useReadContractMultiple({
    contracts: formattedAddresses.map((address) => ({
      address: address as Address,
      abi: ERC20_ABI as Abi,
      functionName: 'name',
    })),
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: formattedAddresses.length > 0,
    },
  })

  const { data: symbols } = useReadContractMultiple({
    contracts: formattedAddresses.map((address) => ({
      address: address as Address,
      abi: ERC20_ABI as Abi,
      functionName: 'symbol',
    })),
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: formattedAddresses.length > 0,
    },
  })

  const { data: decimals } = useReadContractMultiple({
    contracts: formattedAddresses.map((address) => ({
      address: address as Address,
      abi: ERC20_ABI as Abi,
      functionName: 'decimals',
    })),
    query: {
      ...WAGMI_REFETCH_POLICY_NEVER,
      enabled: formattedAddresses.length > 0,
    },
  })

  return useMemo(() => {
    return formattedAddresses.map((tokenAddress, index) => {
      if (typeof tokenAddress !== 'string') return undefined
      if (decimals?.[index]?.status !== 'success' || !decimals?.[index]?.result) return null
      if (symbols?.[index]?.status !== 'success' || !symbols?.[index]?.result) return null
      if (tokenNames?.[index]?.status !== 'success' || !tokenNames?.[index]?.result) return null
      return new Token(
        chainId,
        tokenAddress,
        decimals?.[index]?.result as number,
        symbols?.[index]?.result as string,
        tokenNames?.[index]?.result as string
      )
    })
  }, [formattedAddresses, chainId, decimals, symbols, tokenNames])
}

type TokenMap = { [address: string]: Token }

/**
 * Returns a Token from the tokenAddress.
 * Returns null if token is loading or null was passed.
 * Returns undefined if tokenAddress is invalid or token does not exist.
 */

export function useTokenFromMapOrNetworkInternal(
  tokens: TokenMap,
  tokenAddress?: string | null
): {
  token: Token | null | undefined
  isLoading: boolean
  error: string | undefined
} {
  const address = isAddress(tokenAddress)
  const token: Token | undefined = address ? tokens[address] : undefined
  const {
    token: tokenFromNetwork,
    isLoading: isLoadingFromNetwork,
    error: errorFromNetwork,
  } = useTokenFromNetworkWithState(token ? undefined : address ? address : undefined)

  return {
    token: token ?? tokenFromNetwork,
    isLoading: token ? false : isLoadingFromNetwork,
    error: errorFromNetwork,
  }
}

// Token only
export function useTokenFromMapOrNetwork(tokens: TokenMap, tokenAddress?: string | null): Token | null | undefined {
  const { token } = useTokenFromMapOrNetworkInternal(tokens, tokenAddress)

  return token
}

// Token + state
export function useTokenFromMapOrNetworkWithState(
  tokens: TokenMap,
  tokenAddress?: string | null
): {
  token: Token | null | undefined
  isLoading: boolean
  error: string | undefined
} {
  return useTokenFromMapOrNetworkInternal(tokens, tokenAddress)
}

/**
 * Returns a Token from the tokenAddress.
 * Returns null if token is loading or null was passed.
 * Returns undefined if tokenAddress is invalid or token does not exist.
 */
export function useTokensFromMapOrNetwork(
  tokenMap: TokenMap,
  tokenAddresses?: (string | null | undefined)[]
): (Token | null | undefined)[] {
  const correctTokenAddresses = useMemo(() => {
    return (
      tokenAddresses?.map((address) => isAddress(address)).filter((address): address is string => address !== false) ||
      []
    )
  }, [tokenAddresses])

  const finalTokenMapping = useMemo(() => {
    // .map((address) => address.toLowerCase()) || []
    const tokenMapping: { [address: string]: Token | 'fromNetwork' } = {}

    correctTokenAddresses.forEach((address) => {
      if (address) {
        if (tokenMap[address]) {
          tokenMapping[address] = tokenMap[address]
        } else {
          tokenMapping[address] = 'fromNetwork'
        }
      }
    })
    return tokenMapping
  }, [correctTokenAddresses, tokenMap])

  const onchainTokenAddresses = useMemo(
    () => Object.keys(finalTokenMapping).filter((address) => finalTokenMapping[address] === 'fromNetwork'),
    [finalTokenMapping]
  )

  const tokensFromNetwork = useTokensFromNetwork(onchainTokenAddresses)

  const onchainTokenMap = useMemo(
    () =>
      tokensFromNetwork.reduce((acc, token) => {
        if (token) {
          acc[token.address] = token
        }
        return acc
      }, {} as any),
    [tokensFromNetwork]
  )

  const list = (correctTokenAddresses || []).map((address) =>
    address
      ? finalTokenMapping[address] === 'fromNetwork'
        ? onchainTokenMap[address]
        : finalTokenMapping[address]
      : undefined
  )

  return list
}

/**
 * Returns a Currency from the currencyId.
 * Returns null if currency is loading or null was passed.
 * Returns undefined if currencyId is invalid or token does not exist.
 */
export function useCurrencyFromMap(tokens: TokenMap, currencyId?: string | null): Currency | null | undefined {
  const nativeCurrency = useNativeCurrency()
  const { chainId } = useConnection()
  const isNative = Boolean(nativeCurrency && isNativeCurrencyId(currencyId ?? null, chainId))

  const token = useTokenFromMapOrNetwork(tokens, isNative ? undefined : currencyId)

  if (currencyId === null || currencyId === undefined || !chainId) return null

  // this case so we use our builtin wrapped token instead of wrapped tokens on token lists
  const wrappedNative = nativeCurrency?.wrapped
  if (wrappedNative?.address?.toUpperCase() === currencyId?.toUpperCase()) return wrappedNative

  // fast path for promote token
  const promoteToken = PROMOTE_TOKENS_BY_CHAIN[chainId as SupportedChainId]
  if (promoteToken?.address?.toLowerCase() === currencyId?.toLowerCase()) {
    return promoteToken
  }

  return isNative ? nativeCurrency : token
}
